import clsx from 'clsx'
import { differenceInDays, format } from 'date-fns'
import { Button } from 'gu-ui-kit'
import img from 'images/banners/wallets/img.png'
import img2 from 'images/banners/wallets/img2.png'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useWallets } from 'modules/Finance/Wallets/useWallets'
import Price from '../Price'
import StatusLabel from '../StatusLabel'
import styles from 'modules/Finance/Wallets/subcomponents/Banner/Banner.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'

export const Banner = ({ className = '' }) => {
  const pathGen = useGeneratePathI18n()
  const {
    activeWalletID,
    promisedPayments: {
      data: promisedPayments,
      loaded: promisedPaymentsLoaded,
    },
  } = useWallets()

  const { t } = useTranslation(['wallets'])

  const currentWalletPromisedPayment = useMemo(() => {
    if (activeWalletID !== null && promisedPaymentsLoaded && promisedPayments) {
      return promisedPayments
        .sort((a, b) => a.created_at >= b.created_at)
        .find(
          (promisedPayment) =>
            promisedPayment?.status === 'approved' &&
            promisedPayment?.wallet?.id === activeWalletID &&
            promisedPayment?.repayment_date !== null,
        )
    }

    return null
  }, [promisedPayments, activeWalletID, promisedPaymentsLoaded])

  const warnStatus = useMemo(() => {
    if (currentWalletPromisedPayment?.repayment_date) {
      const difference = differenceInDays(
        new Date(currentWalletPromisedPayment.repayment_date * 1000),
        new Date(),
      )

      switch (true) {
        case difference > 5 && difference < 30:
          return 'warning'
        case difference < 5:
          return 'error'
        case difference > 30:
          return 'info'
      }
    }
  }, [currentWalletPromisedPayment])

  const { is_expired, repayment_amount, wallet, repayment_date } = useMemo(
    () => currentWalletPromisedPayment || {},
    [currentWalletPromisedPayment],
  )

  if (!currentWalletPromisedPayment) {
    return null
  }

  return (
    <div className={styles['banner-wrapper']}>
      <div className={clsx(styles['banner'], className)}>
        <div className={styles['banner__fig']}>
          <picture className={styles['banner__img']}>
            <source srcSet={img2} media="(max-width: 600px)" />
            <img className={styles['banner__img']} src={img} alt="" />
          </picture>
        </div>
        <div
          className={clsx(
            styles['banner__context'],
            styles['banner__context_' + warnStatus],
          )}
        >
          <div className={styles['banner__wrapper-label']}>
            <StatusLabel className={styles['banner__label']}>
              {t('banner.payment.takenLabel')}
            </StatusLabel>
          </div>
          <div className={styles['banner__title']}>
            <Price number={repayment_amount} currency={wallet.currency} />
          </div>
          <div className={styles['banner__summary']}>
            {`${t('banner.payment.taken')} ${!is_expired ? t('nav.by') : ''} ${
              !is_expired && repayment_date !== null
                ? format(repayment_date * 1000, t('date_format.long'))
                : ''
            }`}
          </div>
          <div className={styles['banner__foot']}>
            <Link to={pathGen(ROUTES.BILLING_TOP_UP)}>
              <Button
                className={styles['banner__button']}
                bgColor="basic-primary-white"
                color={'basic-primary-black'}
                size="l"
                variant="primary"
              >
                {t('banner.payment.pay')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

Banner.propTypes = {
  className: PropTypes.string,
}
